// Display helpers for המוצרים המובילים ברכש (the /analysis card and its
// report). The numbers themselves come from the server (lib/topProducts.js);
// nothing here re-derives an amount or a price.

export const TOP_PRODUCTS_REPORT_PATH = '/analysis/reports/top-products';

// One product from one supplier — the server's row identity.
export const topProductKey = (row) => `${row.supplier_id}|${row.product_key}`;

// Which way a price moved over the period. A RISE is the reader's problem (the
// .m-delta is-up / change-pos colours are danger), a fall is good news.
export function changeTone(pct) {
  if (pct === null || pct === undefined || pct === '') return null;
  const n = parseFloat(pct);
  if (Number.isNaN(n)) return null;
  return n > 0 ? 'up' : n < 0 ? 'down' : 'flat';
}

// "+20%" / "-5.3%" / "0%" — the supplier report's delta-chip text, so the two
// screens print a change the same way. Null when there is nothing to compare.
export function formatChangePct(pct) {
  const tone = changeTone(pct);
  if (!tone) return null;
  const n = parseFloat(pct);
  return n > 0 ? `+${n}%` : `${n}%`;
}

// The change a row shows. The server leaves change_pct null both for a single
// purchase AND for a price that did not move (priceTrend), and those must not
// look alike: a flat price is information ("0%"), one purchase is not.
// first_price is set only with two or more priced purchases.
export function rowChangePct(row) {
  if (row?.change_pct != null) return row.change_pct;
  return row?.first_price != null ? '0.0' : null;
}

// Mixed pack sizes under one product (a 1 kg and a 10 kg bag, both "סוכר")
// make min, max and % meaningless. Past this ratio the sheet says so instead of
// pointing at a negotiation that is really a unit mismatch.
export const UNIT_MISMATCH_RATIO = 2.5;

// The product sheet's one line under the current price (owner + product
// review, 21/10/2026 — the min–max bar, the first price and the "highest in
// the period" note were dropped as repeating the % chip and the orders):
//   { kind: 'range', min, max }  "מינ׳ ₪… · מקס׳ ₪…" — the "you once paid
//                                 less" number, which a long order list buries
//   { kind: 'units' }            max/min ≥ UNIT_MISMATCH_RATIO — likely mixed
//                                 pack sizes; says so instead of a range, so
//                                 nobody negotiates over a unit mismatch
//   null                         one purchase, or one price all period (the
//                                 chip's 0% already says it), or no price
export function priceRangeLine(row) {
  if (!row || row.current_price == null || row.first_price == null) return null;
  const { min_price: min, max_price: max } = row;
  if (min == null || max == null || !(min > 0) || !(max > min)) return null;
  if (max / min >= UNIT_MISMATCH_RATIO) return { kind: 'units' };
  return { kind: 'range', min, max };
}

// A spread smaller than this is drawn as "מחיר יציב", not as a bar: on a bar
// scaled to its own min–max, ₪12.30 vs ₪12.40 would look as dramatic as ₪10 vs
// ₪20 (data review, 20/10/2026).
export const FLAT_SPREAD = 0.03;

// What the row's min–max slot shows (owner + designer, 20/10/2026):
//   { kind: 'bar', min, max, pos }  pos = 0 at min … 1 at max (the dot)
//   { kind: 'single' }  one priced purchase — "קנייה אחת"
//   { kind: 'flat' }    min = max, or a spread under FLAT_SPREAD — "מחיר יציב"
//   { kind: 'units' }   max/min ≥ UNIT_MISMATCH_RATIO — likely mixed pack
//                       sizes, so no claim at all ("—")
//   null                no price to place
export function priceRangeBar(row) {
  if (!row || row.current_price == null) return null;
  if (row.first_price == null) return { kind: 'single' };
  const { min_price: min, max_price: max, current_price: cur } = row;
  if (min == null || max == null || !(min > 0)) return null;
  if (max / min >= UNIT_MISMATCH_RATIO) return { kind: 'units' };
  if (!(max > min) || (max - min) / min < FLAT_SPREAD) return { kind: 'flat' };
  const pos = Math.min(1, Math.max(0, (cur - min) / (max - min)));
  return { kind: 'bar', min, max, pos };
}
